import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Webcam from "react-webcam";

import { saveFaceEnrollment } from "./faceEnrollmentSlice";
import {
  detectFaceDescriptorFast,
  createLandmarkTracker,
  yawFromLandmarks,
  pitchFromLandmarks,
  runFrameLoop,
  assessFaceQuality,
  meanDescriptor,
  euclideanDistance,
} from "../../../../lib/faceRecognition";

// Enrollment wajah — metode lama (5 pose, sampel diambil dengan tombol
// "Ambil Sampel"), dengan tambahan:
//   - Pose dibaca CEPAT per frame (pelacak landmark ringan) sehingga indikator
//     pose langsung hijau begitu posisi sesuai; model berat (SSD + descriptor)
//     hanya dijalankan saat tombol ditekan.
//   - Satu sampel = rata-rata BURST_FRAMES frame berurutan yang lolos cek
//     kualitas, ditolak bila tak stabil -> descriptor lebih presisi.
//   - Semua sampel wajib orang yang sama dengan sampel pertama, dan saat
//     disimpan server menolak bila mirip karyawan lain (bisa dipaksa).
// Tanpa verifikasi kedipan / pembacaan mata (dihapus 2026-10-06 atas
// permintaan user: lambat dan tidak terbaca di perangkat).
//
// 2026-10-07 (lebih responsif): model ringan yg sama dgn kiosk (Tiny +
// landmark tiny, ~3-4x lebih cepat drpd SSD), dan sampel diambil OTOMATIS
// begitu pose sesuai ditahan AUTO_HOLD_MS; tombol tetap ada sbg cadangan.

const BURST_FRAMES = 3;
const AUTO_HOLD_MS = 500; // pose hijau ditahan selama ini -> sampel diambil otomatis
const SAME_PERSON_MAX = 0.45; // jarak maks ke sampel pertama (orang yang sama)
const FRAME_SPREAD_MAX = 0.3; // jarak maks frame ke rata-rata dalam satu sampel

// Toleransi pose sengaja longgar: cukup terlihat variasinya, tak perlu tepat.
// ctx: { pitch0, sideSign } dari sampel sebelumnya.
const STEPS = [
  {
    label: "Lihat lurus ke kamera",
    maxYaw: 0.25,
    check: ({ yaw }) => (Math.abs(yaw) > 0.15 ? "Hadapkan wajah lurus ke kamera" : null),
  },
  {
    label: "Sedikit menoleh ke kiri atau kanan",
    maxYaw: 0.5,
    check: ({ yaw }) =>
      Math.abs(yaw) < 0.1 ? "Menoleh sedikit ke samping"
        : Math.abs(yaw) > 0.45 ? "Terlalu menoleh, kembalikan sedikit"
        : null,
  },
  {
    label: "Sedikit menoleh ke arah sebaliknya",
    maxYaw: 0.5,
    check: ({ yaw }, ctx) =>
      Math.abs(yaw) < 0.1 || Math.sign(yaw) === ctx.sideSign ? "Menoleh ke arah sebaliknya"
        : Math.abs(yaw) > 0.45 ? "Terlalu menoleh, kembalikan sedikit"
        : null,
  },
  {
    label: "Sedikit mendongak ke atas",
    maxYaw: 0.3,
    check: ({ yaw, pitch }, ctx) =>
      Math.abs(yaw) > 0.2 ? "Hadapkan wajah lurus, lalu mendongak sedikit"
        : pitch > ctx.pitch0 - 0.06 ? "Mendongak sedikit"
        : null,
  },
  {
    label: "Sedikit menunduk ke bawah",
    maxYaw: 0.3,
    check: ({ yaw, pitch }, ctx) =>
      Math.abs(yaw) > 0.2 ? "Hadapkan wajah lurus, lalu menunduk sedikit"
        : pitch < ctx.pitch0 + 0.06 ? "Menunduk sedikit"
        : null,
  },
];

export default function EnrollWizard({ target, onClose, onSaved, onEnrollOther }) {
  const dispatch = useDispatch();
  const webcamRef = useRef(null);
  const poseRef = useRef(null); // pose terbaru dari pelacak cepat {yaw, pitch}
  const ctxRef = useRef({ pitch0: 0, sideSign: 0 });
  const captureRef = useRef(null); // captureSample terbaru utk dipanggil dari loop pose
  const busyRef = useRef(false);

  const [samples, setSamples] = useState([]); // [{descriptor, distToFirst}]
  const [poseHint, setPoseHint] = useState("Menyiapkan kamera…");
  const [poseOk, setPoseOk] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState("");
  const [phase, setPhase] = useState("capture"); // capture | saving | duplicate
  const [duplicate, setDuplicate] = useState(null);

  const step = samples.length;

  // ── Pembacaan pose cepat per frame (tanpa descriptor) ──
  useEffect(() => {
    if (phase !== "capture" || step >= STEPS.length) return;
    const track = createLandmarkTracker({ tiny: true });
    let frame = 0;
    let okSince = null;
    setPoseOk(false); // langkah baru: tunggu pose terbaca dulu
    const stop = runFrameLoop(async () => {
      const video = webcamRef.current?.video;
      if (!video || video.readyState !== 4) return;
      const t = await track(video);
      if (!t) {
        poseRef.current = null;
        okSince = null;
        setPoseOk(false);
        setPoseHint("Wajah tidak terdeteksi — posisikan wajah di tengah kamera");
        return;
      }
      const pose = { yaw: yawFromLandmarks(t.landmarks), pitch: pitchFromLandmarks(t.landmarks) };
      poseRef.current = pose;
      const hint = STEPS[step].check(pose, ctxRef.current);
      okSince = hint ? null : okSince ?? Date.now();
      if (!hint && !busyRef.current && Date.now() - okSince >= AUTO_HOLD_MS) {
        okSince = null;
        captureRef.current?.();
        return;
      }
      if (frame++ % 2) return; // perbarui tampilan ~15x/detik, cukup halus
      setPoseOk(!hint);
      setPoseHint(hint || "Posisi sesuai — tahan sebentar…");
    });
    return stop;
  }, [phase, step]);

  const restart = () => {
    ctxRef.current = { pitch0: 0, sideSign: 0 };
    setSamples([]);
    setDuplicate(null);
    setCaptureError("");
    setPhase("capture");
  };

  const save = async (list, force = false) => {
    setPhase("saving");
    try {
      const res = await dispatch(
        saveFaceEnrollment({ Nik: target.Nik, Descriptors: list.map((s) => s.descriptor), Force: force })
      ).unwrap();
      if (res?.status === "Duplicate") {
        setDuplicate(res.data);
        setPhase("duplicate");
        return;
      }
      toast.success(`Enrollment wajah ${target.NamaKaryawan} selesai (${list.length} sampel)`, {
        position: "bottom-right",
      });
      onSaved();
    } catch (e) {
      toast.error(e?.message || "Gagal menyimpan enrollment", { position: "bottom-right" });
      setPhase("capture");
    }
  };

  // ── Ambil satu sampel: burst beberapa frame, dirata-rata ──
  const captureSample = async () => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState !== 4 || busyRef.current) return;
    busyRef.current = true;
    setCapturing(true);
    setCaptureError("");
    try {
      const frames = [];
      for (let tries = 0; frames.length < BURST_FRAMES && tries < BURST_FRAMES + 3; tries++) {
        const r = await detectFaceDescriptorFast(video);
        if (!r) continue;
        const q = assessFaceQuality(r, video, { maxYaw: STEPS[step].maxYaw, minFaceWidth: 0.15, minScore: 0.5 });
        if (q) {
          setCaptureError(q.hint);
          continue;
        }
        frames.push(Array.from(r.descriptor));
      }
      if (frames.length < BURST_FRAMES) {
        setCaptureError((prev) => prev || "Wajah tidak terbaca jelas — pastikan wajah terlihat penuh & pencahayaan cukup.");
        return;
      }
      const mean = meanDescriptor(frames);
      if (frames.some((d) => euclideanDistance(d, mean) > FRAME_SPREAD_MAX)) {
        setCaptureError("Gambar kurang stabil — tahan posisi lebih diam, lalu ambil lagi.");
        return;
      }
      const first = samples[0]?.descriptor;
      const distToFirst = first ? euclideanDistance(mean, first) : 0;
      if (first && distToFirst > SAME_PERSON_MAX) {
        setCaptureError("Wajah berbeda dari sampel pertama — pastikan hanya karyawan ini di depan kamera.");
        return;
      }
      const pose = poseRef.current;
      if (step === 0 && pose) ctxRef.current.pitch0 = pose.pitch;
      if (step === 1 && pose) ctxRef.current.sideSign = Math.sign(pose.yaw);

      const next = [...samples, { descriptor: mean, distToFirst }];
      setSamples(next);
      setCaptureError("");
      if (next.length >= STEPS.length) await save(next);
    } catch (e) {
      setCaptureError("Gagal memproses wajah, coba lagi.");
    } finally {
      busyRef.current = false;
      setCapturing(false);
    }
  };
  captureRef.current = phase === "capture" && step < STEPS.length ? captureSample : null;

  const maxDist = samples.reduce((mx, s) => Math.max(mx, s.distToFirst), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">Enrollment Wajah — {target.NamaKaryawan}</h3>
          <p className="text-xs text-gray-500">
            Ikuti pose tiap langkah — sampel diambil otomatis saat indikator hijau (atau tekan Ambil Sampel). Video hanya diproses di browser ini.
          </p>
        </div>

        <div className="px-6 py-4">
          <div className="flex gap-1.5 mb-3">
            {STEPS.map((s, i) => (
              <div key={s.label} className="flex-1">
                <div className={`h-1.5 rounded ${i < step ? "bg-green-500" : i === step ? "bg-orange-400" : "bg-gray-200"}`} />
                <div className={`mt-1 text-[10px] leading-tight ${i === step ? "text-gray-800 font-semibold" : "text-gray-400"}`}>
                  {i + 1}. {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="relative bg-black rounded-lg overflow-hidden mx-auto" style={{ width: "100%", maxWidth: 420, aspectRatio: "4 / 3" }}>
            <Webcam
              ref={webcamRef}
              audio={false}
              mirrored
              videoConstraints={{ width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" }}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {phase === "capture" && step < STEPS.length && (
              <div className="absolute bottom-0 inset-x-0 bg-black/60 px-3 py-2 text-center">
                <div className="text-white text-sm font-semibold">
                  Sampel {step + 1} dari {STEPS.length}: {STEPS[step].label}
                </div>
                <div className={`text-xs ${poseOk ? "text-green-300" : "text-amber-300"}`}>
                  {capturing ? "Memproses, tahan posisi…" : poseHint}
                </div>
              </div>
            )}
            {phase === "capture" && (
              <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${poseOk ? "bg-green-400" : "bg-amber-400"}`} />
            )}
          </div>

          {captureError && phase === "capture" && <p className="text-xs text-red-600 mt-2">{captureError}</p>}

          {phase === "saving" && (
            <p className="mt-3 text-sm text-gray-700">
              Menyimpan {samples.length} sampel… (konsistensi: jarak terjauh ke sampel pertama {maxDist.toFixed(3)}, batas {SAME_PERSON_MAX})
            </p>
          )}

          {phase === "duplicate" && duplicate && (
            <div className="mt-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 space-y-2">
              <p>
                <strong>Wajah ini mirip dengan {duplicate.NamaKaryawan} ({duplicate.Nik})</strong> — sampel ke-{duplicate.SampleNo}
                {" "}miliknya berjarak {duplicate.Distance} (kiosk menganggap orang yang sama di bawah 0,5). Data{" "}
                <strong>belum disimpan</strong>.
              </p>
              {duplicate.Inconsistent ? (
                <p>
                  Kemungkinan besar <strong>data {duplicate.NamaKaryawan} yang bermasalah</strong>: sampel wajahnya sendiri tidak
                  konsisten (sebaran {duplicate.Sebaran}, normal di bawah 0,45). Enroll ulang {duplicate.NamaKaryawan} terlebih
                  dahulu, lalu ulangi enroll {target.NamaKaryawan}.
                </p>
              ) : (
                <p>
                  Data {duplicate.NamaKaryawan} sendiri konsisten. Pastikan yang di depan kamera benar {target.NamaKaryawan}.
                  Bila memang dua orang berbeda yang mirip (mis. kembar), boleh tetap disimpan — kiosk akan meminta wajah
                  ditahan bila ragu di antara keduanya.
                </p>
              )}
            </div>
          )}

          <p className="text-[11px] text-gray-400 mt-2">
            Tips: wajah terang dari depan, tanpa masker/kacamata gelap, hanya satu orang di depan kamera.
          </p>
        </div>

        <div className="flex justify-between gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={restart} disabled={phase === "saving" || capturing}
            className="px-4 py-2 rounded-lg border text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40">
            Ulangi dari Awal
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={phase === "saving"}
              className="px-4 py-2 rounded-lg border text-sm font-medium text-gray-600 hover:bg-gray-100">
              Batal
            </button>
            {phase === "duplicate" && duplicate?.Inconsistent && onEnrollOther && (
              <button onClick={() => onEnrollOther({ Nik: duplicate.Nik, NamaKaryawan: duplicate.NamaKaryawan })}
                className="px-4 py-2 rounded-lg text-white text-sm font-semibold bg-orange-500 hover:bg-orange-600">
                Enroll Ulang {duplicate.NamaKaryawan}
              </button>
            )}
            {phase === "duplicate" ? (
              <button onClick={() => save(samples, true)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${duplicate?.Inconsistent
                  ? "border border-red-300 text-red-700 bg-white hover:bg-red-50"
                  : "text-white bg-red-600 hover:bg-red-700"}`}>
                Tetap Simpan (orang berbeda)
              </button>
            ) : (
              <button onClick={captureSample} disabled={phase !== "capture" || capturing || !poseOk}
                className="px-4 py-2 rounded-lg text-white text-sm font-semibold bg-sky-600 hover:bg-sky-700 disabled:opacity-40">
                {capturing || phase === "saving" ? "Memproses…" : "Ambil Sampel"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
